import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { artifactUrl, fetchArtifactText } from '@/lib/api/quality.js';
import type { DocumentEvidenceSummary } from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

import { AuthImage } from '../AuthMedia.js';

/** The line above a step's documents (MEDA-1139). */
export const DOCUMENTS_LINE = 'Documents the agent read during this step:';

const NO_EXCERPT = '(no text was recorded for this read)';

/** How many characters of the SHA-256 the line shows; the title carries all of it. */
const SHORT_HASH_LENGTH = 12;

type ExcerptState =
  { kind: 'loading' } | { kind: 'failed' } | { kind: 'loaded'; text: string };

/**
 * The documents one step read or rendered (PDFs, spreadsheets), one line each: file, short hash,
 * pages or sheet and rows. A line expands to
 * the masked text excerpt and, for a render, the page image, both loaded from the run's artifacts
 * on first open. The worker masked the record before storing it. Renders nothing when the step
 * opened no document, and also for a value that is not an array: the report is cast, not validated.
 */
export function DocumentEvidence({
  runId,
  documents,
}: {
  runId: string;
  documents?: DocumentEvidenceSummary[];
}): JSX.Element | null {
  if (!Array.isArray(documents) || documents.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1 pl-3.5">
      <p className="text-xs text-muted-foreground/80">{DOCUMENTS_LINE}</p>
      {documents.map((document, index) => (
        <DocumentCard
          key={document.artifact_key ?? `${document.file}-${index}`}
          runId={runId}
          document={document}
        />
      ))}
    </div>
  );
}

function DocumentCard({
  runId,
  document,
}: {
  runId: string;
  document: DocumentEvidenceSummary;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [excerpt, setExcerpt] = useState<ExcerptState>({ kind: 'loading' });
  const pages = Array.isArray(document.pages) ? document.pages : [];
  const isRender = Boolean(document.png || document.png_key);
  const excerptKey = isRender ? undefined : document.artifact_key;

  useEffect(() => {
    if (!open || !excerptKey) return;
    let cancelled = false;
    fetchArtifactText(runId, excerptKey)
      .then((text) => {
        if (text === null) throw new Error('document record unavailable');
        if (!cancelled) setExcerpt({ kind: 'loaded', text: excerptOf(text) });
      })
      .catch(() => {
        if (!cancelled) setExcerpt({ kind: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [open, runId, excerptKey]);

  return (
    <div className="rounded-md border border-border bg-muted/30 text-xs">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full min-w-0 items-center gap-1.5 px-2 py-1 text-left font-mono"
      >
        <ChevronRightIcon
          className={cn(
            'size-3 shrink-0 transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden
        />
        <span className="min-w-0 truncate font-semibold" title={document.file}>
          {document.file}
        </span>
        <span
          className="shrink-0 text-muted-foreground"
          title={document.sha256}
        >
          {document.sha256.slice(0, SHORT_HASH_LENGTH)}
        </span>
        <span className="ml-auto shrink-0 text-muted-foreground">
          {locationLabel(document)}
        </span>
      </button>
      {open ? (
        <div className="space-y-1.5 border-t border-border px-2 py-1.5">
          {excerptKey ? (
            <div>
              <p className="text-muted-foreground/70">Text excerpt</p>
              {excerpt.kind === 'failed' ? (
                <p className="text-destructive">
                  The document record could not be loaded.
                </p>
              ) : (
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-background/60 p-1.5 font-mono">
                  {excerpt.kind === 'loaded' ? excerpt.text : '…'}
                </pre>
              )}
            </div>
          ) : null}
          {document.png_key ? (
            <AuthImage
              src={artifactUrl(runId, document.png_key)}
              alt={`Page ${pages.join(', ')} of ${document.file}`}
              className="max-h-[32rem] w-full max-w-xl rounded border border-border object-contain"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Where the read happened: `sheet Stops, rows 1–40` or `rows 1–200` for a spreadsheet,
 * `page 2` or `pages 1, 2` for a PDF, `no rows` for an empty spreadsheet read.
 */
export function locationLabel(
  document: Pick<DocumentEvidenceSummary, 'pages' | 'sheet' | 'rows'>,
): string {
  const { rows, sheet } = document;
  if (Array.isArray(rows) && rows.length === 2) {
    const range = `rows ${rows[0]}–${rows[1]}`;
    return sheet ? `sheet ${sheet}, ${range}` : range;
  }
  const pages = Array.isArray(document.pages) ? document.pages : [];
  if (pages.length === 0) return sheet ? `sheet ${sheet}, no rows` : 'no rows';
  return `${pages.length === 1 ? 'page' : 'pages'} ${pages.join(', ')}`;
}

/** The record's `text_excerpt`, or the empty-state line when it is missing or the record is bad. */
function excerptOf(text: string): string {
  try {
    const record: unknown = JSON.parse(text);
    const excerpt = (record as { text_excerpt?: unknown } | null)?.text_excerpt;
    return typeof excerpt === 'string' && excerpt ? excerpt : NO_EXCERPT;
  } catch {
    return NO_EXCERPT;
  }
}
